import { barColors, barColorsForLastTerms } from '../../constants'

export interface OptionType {
  name: string
  counts?: number[]
  percents?: number[]
}

export const formatSeriesDisplayName = (seriesName: string, titles: string[]): string => {
  const baseName = titles[0] || ''
  if (seriesName === baseName) return baseName
  if (seriesName.startsWith(baseName)) {
    // Remove the base name and any separator (like ' -', ':', etc.)
    const suffix = seriesName.slice(baseName.length).replace(/^\s*[-:–—]\s*/, '')
    return suffix || seriesName
  }
  return seriesName
}

export const formatTooltip = ({ params, titles, keys, options, showPercents }: {
  params: any
  titles: string[]
  keys: string[]
  options: OptionType[]
  showPercents: boolean
}): string => {
  const paramArr = Array.isArray(params) ? params : [params]
  if (!paramArr.length) return ''
  const xAxisLabel = paramArr[0].axisValue || paramArr[0].name
  const lines = paramArr.map((p: any) => {
    const groupIdx = titles.indexOf(p.seriesName)
    const optionIdx = keys.indexOf(p.axisValue || p.name)
    const actualValue = options[optionIdx]?.counts?.[groupIdx] ?? 0
    const percentValue = options[optionIdx]?.percents?.[groupIdx] ?? 0
    const displayName = formatSeriesDisplayName(p.seriesName, titles)
    let percentDisplay = ''
    if (showPercents) {
      const percentNum = percentValue * 100
      percentDisplay = Number.isInteger(percentNum)
        ? percentNum.toString()
        : percentNum.toFixed(2)
      return actualValue !== 0
        ? `${p.marker}${displayName}: <b>${actualValue}</b> (${percentDisplay}%)`
        : `${p.marker}${displayName}: <b>${actualValue}</b>`
    }
    return `${p.marker}${displayName}: <b>${actualValue}</b>`
  })
  return `
    <div style="padding:2px 6px 4px">
      <b><center>${xAxisLabel}</center></b>${lines.join('<br/>')}
    </div>
  `
}

export const getFilteredOptions = (options: OptionType[], showSlice: boolean): OptionType[] =>
  showSlice
    ? options.map(option => ({
      ...option,
      counts: option.counts?.slice(1),
      percents: option.percents?.slice(1)
    }))
    : options

export const getBarChartColors = (selectedTrend: number): string[] =>
  selectedTrend === 2 ? barColorsForLastTerms : barColors

export const buildBarSeries = ({
  groupCount,
  titles,
  filteredOptions,
  showPercents,
  theme,
  showSlice,
  barColors
}: {
  groupCount: number
  titles: string[]
  filteredOptions: OptionType[]
  showPercents: boolean
  theme: any
  showSlice: boolean
  barColors: string[]
}): any[] =>
  Array.from({ length: groupCount }, (_, groupIdx) => {
    if (showSlice && groupIdx === 0) return null
    const filteredIdx = showSlice ? groupIdx - 1 : groupIdx
    return {
      name: titles[groupIdx],
      type: 'bar',
      data: filteredOptions.map(option =>
        showPercents
          ? Number(((option.percents?.[filteredIdx] ?? 0) * 100).toFixed(2))
          : option.counts?.[filteredIdx] ?? 0
      ),
      itemStyle: {
        color: barColors[groupIdx % barColors.length],
        borderRadius: [10, 10, 0, 0]
      },
      label: {
        show: true,
        position: 'top',
        fontSize: 14,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        formatter: (params: any) => {
          if ((showSlice ? groupCount - 1 : groupCount) > 1 || !params.value) return ''
          return showPercents ? `${Number(params.value).toFixed(0)}%` : `${Number(params.value).toFixed(0)}`
        }
      },
      barMaxWidth: 80
    }
  }).filter(Boolean)

export const getContrastTextColor = (bgColor: string, lightText: string, darkText: string): string => {
  // Use white text for dark backgrounds, black for light backgrounds
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b
  return luminance < 140 ? lightText : darkText
}
